import {
  BusEventType,
  ChatCustomElement,
  PanelType,
  ViewType,
  type BusEventPreSend,
  type BusEventViewChange,
  type ChatInstance,
  type CustomSendMessageOptions,
  type HistoryItem,
  type MessageRequest,
} from '@carbon/ai-chat';
import { Button } from '@carbon/react';
import { AiLabel } from '@carbon/react/icons';
// Our own mark identifies the assistant inside the transcript. Everywhere else
// uses Carbon's AI label, so the affordance that opens the panel matches the
// AI explainability control in its header.
import InsightsAgentSvg from 'images/insights-agent-logo.svg';
import { useAtom } from 'jotai';
import { ReactNode, useCallback, useMemo, useRef, useState } from 'react';

import {
  activeConversationIdAtomFamily,
  agentPanelOpenAtomFamily,
  agentPanelWidthAtomFamily,
} from 'atoms/agent-chat-atoms';

import { AgentDock } from './agent-dock';
import { fetchAgentMessages, streamAgent, type AgentPendingState } from './api';
import type { AgentDefinition } from './agent-definition';
import { ConversationList } from './carbon/conversation-list';
import { serverMessagesToHistory } from './carbon/history';
import { createStreamTranslator } from './carbon/stream-adapter';
import { agentScopeKey, type AgentSubject, type ToolStatus } from './types';
import styles from './agent-surface.module.scss';

// Opens/closes the panel for an agent. Shared so a trigger can live anywhere.
export function useAgentPanel(agent: AgentDefinition) {
  return useAtom(agentPanelOpenAtomFamily(agent.agentType));
}

// Width the dock currently occupies, or 0 when closed. Host pages pad their own
// content by this because the dock is `position: fixed` and reserves no space.
export function useAgentDockWidth(agent: AgentDefinition): number {
  const [open] = useAtom(agentPanelOpenAtomFamily(agent.agentType));
  const [width] = useAtom(agentPanelWidthAtomFamily(agent.agentType));
  return open ? width : 0;
}

// Button that opens the agent's panel. Pages wanting their own trigger can use
// `useAgentPanel` directly instead.
export const AgentTriggerButton = ({
  agent,
  label,
  size = 'sm',
}: {
  agent: AgentDefinition;
  label?: string;
  /**
   * Carbon button height, to match the cluster this trigger sits in. A prop rather
   * than a field on the agent definition: the same agent appears in headers built at
   * different heights — EVAL_AGENT sits beside `sm` buttons on the eval-3 workspace
   * action bar and `md` ones in the eval 3 title bar — so the surrounding layout is
   * what decides, not the agent.
   *
   * Defaults to `sm`, which is what every host but the eval 3 title bar uses.
   */
  size?: 'sm' | 'md' | 'lg';
}) => {
  const [open, setOpen] = useAgentPanel(agent);
  return (
    <Button
      kind={agent.triggerKind ?? 'tertiary'}
      size={size}
      renderIcon={AiLabel}
      // The button is the panel's toggle, and it renders selected while the
      // panel is open, so clicking it again has to close it.
      isSelected={open}
      aria-expanded={open}
      onClick={() => setOpen((isOpen) => !isOpen)}
    >
      <span>{label ?? agent.triggerLabel}</span>
    </Button>
  );
};

export type AgentSurfaceProps = {
  agent: AgentDefinition;
  /** GET hydrates / POST sends, scoped to the record the page is about. */
  agentMessagesHref: string;
  /** The record this page's conversations belong to. Omit only for an agent
   * that is not about any one record. */
  subject?: AgentSubject;
  /** Line under the title, e.g. "Ask about MR-K · Courthouse". */
  subtitle?: string;
  /** Shown in the AI explainability popover. */
  model?: string | null;
  /** Set to make the transcript read-only with this message, e.g. while a
   * workspace recalculates. */
  disabledReason?: string | null;
  /** Called once the chat is live, for hosts that write into the transcript. */
  onInstanceReady?: (instance: ChatInstance) => void;
  /**
   * Called as each tool finishes, for hosts that act on a tool's result rather
   * than only rendering it — e.g. a tool that stages an edit onto the page for
   * the user to apply. Hosts must ignore tools they don't handle; every tool the
   * agent runs comes through here, not just the host's own.
   *
   * `status` is the authority on whether the tool succeeded. Do NOT look for a
   * `success` key on `output`: the server strips it on the success path
   * (AgentConversation#split_output_and_error) precisely because `status` already
   * carries it, so a host gating on `output.success` silently ignores every
   * successful result.
   */
  onToolResult?: (
    name: string | null,
    output: unknown,
    status: ToolStatus
  ) => void;
  /**
   * The page's unsaved edits, read at SEND time rather than at render.
   *
   * A function, not a value: the user can edit the grid between renders, and what matters
   * is the state when the message goes out. Hosts that hold no unsaved state omit this —
   * the surface then reports `reported: false`, which tells the server the page could not
   * speak for its own edits rather than that it had none.
   */
  pendingState?: () => AgentPendingState;
  /** Rendered above the transcript (e.g. the conversation list). */
  children?: ReactNode;
};

// A resizable dock hosting Carbon AI Chat. Carbon owns the transcript, composer,
// home screen, scrolling and a11y; this owns what Carbon has no opinion about —
// our SSE transport, the tool-call response type, and hydration.
export const AgentSurface = ({
  agent,
  agentMessagesHref,
  subject: subjectProp,
  subtitle,
  model,
  disabledReason,
  onInstanceReady,
  onToolResult,
  pendingState,
  children,
}: AgentSurfaceProps) => {
  const [open, setOpen] = useAgentPanel(agent);
  const [width, setWidth] = useAtom(agentPanelWidthAtomFamily(agent.agentType));
  // Hosts pass the subject as a literal, so it is a fresh object every render.
  // Rebuild it from its primitives or anything keyed on it — the conversation
  // fetch especially — re-runs on every render.
  const subjectType = subjectProp?.type;
  const subjectId = subjectProp?.id;
  const subject = useMemo(() => {
    if (!subjectType || subjectId === undefined || subjectId === null) {
      return undefined;
    }
    return { type: subjectType, id: subjectId };
  }, [subjectType, subjectId]);
  const scopeKey = agentScopeKey(agent.agentType, subject);
  const [activeConversationId, setActiveConversationId] = useAtom(
    activeConversationIdAtomFamily(scopeKey)
  );

  const [instance, setInstance] = useState<ChatInstance | null>(null);
  // Bus handlers are registered once at boot, so they must call through a ref —
  // a captured callback still sees `instance === null` and silently no-ops.
  const instanceRef = useRef<ChatInstance | null>(null);
  // Set by the conversation list so the panel can refetch when it opens.
  const refreshConversationsRef = useRef<(() => void) | null>(null);

  // Read through a ref inside async callbacks so a stream that outlives a
  // conversation switch still posts to the thread it was sent from.
  const conversationRef = useRef<number | null>(activeConversationId);
  conversationRef.current = activeConversationId;

  // Hosts pass this as a literal, so it changes identity every render. Reading
  // it through a ref keeps it out of `customSendMessage`'s dependencies, which
  // would otherwise rebuild the send handler mid-stream.
  const onToolResultRef = useRef(onToolResult);
  onToolResultRef.current = onToolResult;
  const pendingStateRef = useRef(pendingState);
  pendingStateRef.current = pendingState;

  // The endpoint and the error copy go through refs for a STRONGER reason than
  // convenience: Carbon takes `messaging` ONCE, at startup (see the note on
  // `customLoadHistory`). Anything that rebuilds that object afterwards is at
  // best ignored and at worst leaves the chat wired to a handler Carbon no
  // longer calls.
  //
  // `agentMessagesHref` does exactly that. The eval page appends
  // `?calculation_id=<open run>` to it, so the href — and with it
  // `customSendMessage`, and with it `messaging` — changes identity the moment
  // the open run resolves, and again every time the user calculates or switches
  // runs. That is precisely when staged changes stopped reaching the page: the
  // send handler Carbon was still calling was not the one wired to the host.
  //
  // Held in refs, `messaging` is built once and stays valid for the component's
  // life, while every send still reads the CURRENT run's endpoint.
  const agentMessagesHrefRef = useRef(agentMessagesHref);
  agentMessagesHrefRef.current = agentMessagesHref;
  const errorCopyRef = useRef(agent.errorCopy);
  errorCopyRef.current = agent.errorCopy;

  const customSendMessage = useCallback(
    async (
      request: MessageRequest,
      options: CustomSendMessageOptions,
      chat: ChatInstance
    ) => {
      const text = request.input.text?.trim();
      if (!text) return;

      const translator = createStreamTranslator({
        responseId: crypto.randomUUID(),
        emit: (chunk) => chat.messaging.addMessageChunk(chunk),
        errorCopy: errorCopyRef.current,
        onConversationId: (id) => {
          conversationRef.current = id;
          setActiveConversationId(id);
        },
      });

      // A tool call can run for tens of seconds with nothing on screen — the
      // chain of thought is collapsed by default, so the transcript looks
      // stalled. Name the running tool in Carbon's loading indicator instead.
      let loadingDepth = 0;
      const showWorking = (label: string) => {
        loadingDepth += 1;
        chat.updateIsMessageLoadingCounter('increase', label);
      };
      const clearWorking = () => {
        if (loadingDepth === 0) return;
        loadingDepth -= 1;
        chat.updateIsMessageLoadingCounter('decrease');
      };

      try {
        await streamAgent(
          agentMessagesHrefRef.current,
          {
            message: text,
            conversation_id: conversationRef.current,
            new: conversationRef.current === null,
            // Read here, not captured above: see `pendingState` on the props.
            pending_state: pendingStateRef.current
              ? pendingStateRef.current()
              : { reported: false },
          },
          (event) => {
            if (event.event === 'tool_call_start') {
              showWorking(`Running ${event.data.name}…`);
            }
            if (event.event === 'tool_call_complete') {
              clearWorking();
              const handler = onToolResultRef.current;
              // A staging tool's result is only useful if a host is listening. When no
              // handler is attached the optional call below does nothing AT ALL — and
              // that silence is indistinguishable from a change being applied, which is
              // how a dropped stage hid: the transcript says "press Calculate", the page
              // has nothing pending, and not one line is logged anywhere.
              //
              // The host's own handler cannot report this, because the whole failure is
              // that the host never runs. So it is reported here, at the only point that
              // can see it.
              if (!handler && event.data.name?.startsWith('eval_stage_')) {
                console.warn(
                  '[agent-chat] staging tool result dropped — no onToolResult handler attached',
                  { name: event.data.name, status: event.data.status }
                );
              }
              // A host that acts on tool output must not be able to break the
              // transcript: a throw here would abort the stream mid-reply.
              try {
                handler?.(
                  event.data.name,
                  event.data.output,
                  event.data.status
                );
              } catch (hostError) {
                console.error(
                  '[agent-chat] onToolResult handler threw',
                  hostError
                );
              }
            }
            translator.handle(event);
          },
          options.signal
        );
        await translator.finish();
      } catch (error) {
        // Carbon aborts the signal when the user hits stop (or on its own
        // timeout); that is a deliberate stop, not a failure to report.
        if (options.signal?.aborted) {
          await translator.abandon({ stopped: true });
          return;
        }
        // Keep whatever streamed, then rethrow: Carbon marks the user's
        // message as failed and offers its own retry rather than us printing
        // the error as if the assistant had said it.
        await translator.abandon();
        throw error;
      } finally {
        // A run that errors or is stopped mid-tool would otherwise leave the
        // indicator running forever.
        while (loadingDepth > 0) clearWorking();
      }
    },
    // Stable for the component's life — see the refs above. `messaging` must not
    // change identity after Carbon has taken it.
    [setActiveConversationId]
  );

  // Fetch a thread and adopt whichever conversation the server resolved to.
  const loadThread = useCallback(
    async (conversationId: number | null): Promise<HistoryItem[]> => {
      try {
        const response = await fetchAgentMessages(
          agentMessagesHrefRef.current,
          conversationId
        );
        if (response.conversationId) {
          conversationRef.current = response.conversationId;
          setActiveConversationId(response.conversationId);
        }
        return serverMessagesToHistory(response.messages);
      } catch {
        // Hydration miss is soft: start a fresh thread rather than block chat.
        return [];
      }
    },
    [setActiveConversationId]
  );

  const customLoadHistory = useCallback(
    () => loadThread(conversationRef.current),
    [loadThread]
  );

  // Switching threads clears Carbon's transcript and re-seeds it from our
  // server, which is the only source of truth for a conversation's messages.
  // Carbon only calls customLoadHistory at startup, so this re-hydrates by hand.
  const selectConversation = useCallback(
    async (conversationId: number) => {
      if (conversationId === conversationRef.current) return;
      conversationRef.current = conversationId;
      setActiveConversationId(conversationId);
      if (!instance) return;
      await instance.messaging.clearConversation();
      await instance.messaging.insertHistory(await loadThread(conversationId));
    },
    [instance, loadThread, setActiveConversationId]
  );

  // Carbon leaves the history panel open after a row is clicked, so the
  // transcript swaps behind it and the click looks like it did nothing.
  const closeHistoryPanel = useCallback(() => {
    instanceRef.current?.customPanels?.getPanel(PanelType.HISTORY)?.close();
  }, []);

  const startNewChat = useCallback(async () => {
    conversationRef.current = null;
    setActiveConversationId(null);
    await instanceRef.current?.messaging.clearConversation();
  }, [setActiveConversationId]);

  const messaging = useMemo(
    () => ({ customSendMessage, customLoadHistory }),
    [customSendMessage, customLoadHistory]
  );

  const homescreen = useMemo(() => {
    // The home screen belongs to a brand-new thread. Carbon reopens it whenever
    // the transcript is cleared — including when we clear before re-hydrating a
    // selected conversation — and exposes no API to close it, so switch it off
    // whenever a conversation is in play.
    const hasStarters =
      Boolean(agent.starters?.length) && activeConversationId === null;
    return {
      isOn: hasStarters,
      greeting: subtitle,
      // The home screen belongs to a fresh conversation only — once the thread
      // is underway there is nothing to go back to, so drop the return
      // navigation (the header button and the "Return to conversation" chip).
      disableReturn: true,
      starters: {
        isOn: hasStarters,
        // Labels stay short; `onBeforeRender` swaps the label for the full
        // prompt on pre:send so the agent still receives the long form.
        buttons: (agent.starters ?? []).map((starter) => ({
          label: starter.title,
        })),
      },
    };
  }, [activeConversationId, agent.starters, subtitle]);

  // `isReadonly` would also grey out option buttons and feedback controls in
  // past messages; the pause only concerns the composer.
  const inputConfig = useMemo(
    () => ({ isDisabled: Boolean(disabledReason) }),
    [disabledReason]
  );

  // The header pairs a product-name prefix with the agent name. An agent whose own name
  // already leads with the product ("Insights Agent") would render it twice, so those
  // declare `prefixNameWithProduct: false` rather than being detected by their name.
  const prefixWithProduct = agent.prefixNameWithProduct ?? true;

  // Author label on every assistant message — matches the header, so a message
  // in the transcript names the same agent the panel is titled for.
  const assistantName =
    agent.assistantName ??
    (prefixWithProduct ? `Insights ${agent.agentName}` : agent.agentName);

  // Carbon's defaults name IBM watsonx throughout — the author label, the AI
  // popover, and the screen-reader announcements. Ours is Insights.
  const strings = useMemo(
    () => ({
      ...(agent.inputPlaceholder
        ? { input_placeholder: agent.inputPlaceholder }
        : {}),
    }),
    [agent.inputPlaceholder]
  );

  const header = useMemo(
    () => ({
      // Carbon renders `title` first and `name` after it in bold, so the
      // product name leads and the agent name carries the weight.
      title: prefixWithProduct ? 'Insights' : '',
      name: agent.agentName,
      showAiLabel: true,
      // Keep Carbon's AI label, drop its stock "Powered by IBM watsonx" body —
      // the popover content comes from our own slot below.
      hideDefaultAiLabelContent: true,
    }),
    [agent.agentName, prefixWithProduct]
  );

  // Carbon's built-in history panel supplies the Options menu (New chat /
  // View chats) and the sliding Chats panel; we only supply its contents.
  const history = useMemo(() => ({ isOn: true }), []);

  // The dock's own trigger opens this, so Carbon's floating launcher would be a
  // second, redundant way in — and it renders over the page.
  const launcher = useMemo(() => ({ isOn: false }), []);

  const renderWriteableElements = useMemo(
    () => ({
      historyPanelElement: (
        <ConversationList
          agentType={agent.agentType}
          scopeKey={scopeKey}
          subject={subject}
          activeConversationId={activeConversationId}
          onSelect={(id) => {
            closeHistoryPanel();
            void selectConversation(id);
          }}
          onDeleteActive={() => void startNewChat()}
          onNewChat={() => {
            closeHistoryPanel();
            void startNewChat();
          }}
          onClose={closeHistoryPanel}
          registerRefresh={(refresh) => {
            refreshConversationsRef.current = refresh;
          }}
        />
      ),
      // Carbon disables the composer; it has nowhere to say why, so the reason
      // goes in its own slot above the input. The home screen uses a different
      // slot, so a fresh thread needs both.
      beforeInputElement: disabledReason ? (
        <p className={styles.disabledNotice}>{disabledReason}</p>
      ) : undefined,
      homeScreenBeforeInputElement: disabledReason ? (
        <p className={styles.disabledNotice}>{disabledReason}</p>
      ) : undefined,
      explainabilityPopoverContent: (
        <div className={styles.aiExplained}>
          <p className={styles.aiExplainedTitle}>AI Explainability</p>
          <p>
            Replies are written by an AI model reading this record&rsquo;s own
            data — interpretations, extractions and document text. Every tool it
            used is listed with the answer so you can check the source.
          </p>
          <p>
            AI can misread a document or state something with more confidence
            than the evidence supports. Verify anything you act on.
          </p>
          {model && (
            <p className={styles.aiExplainedModel}>
              Model: <code>{model}</code>
            </p>
          )}
        </div>
      ),
    }),
    [
      agent.agentType,
      activeConversationId,
      closeHistoryPanel,
      disabledReason,
      model,
      scopeKey,
      subject,
      selectConversation,
      startNewChat,
    ]
  );

  // Starter buttons send their short label; expand it to the real prompt before
  // it reaches the server.
  const handleBeforeRender = useCallback(
    (chat: ChatInstance) => {
      instanceRef.current = chat;
      setInstance(chat);
      chat.on({
        type: BusEventType.HISTORY_PANEL_PRE_OPEN,
        handler: () => refreshConversationsRef.current?.(),
      });
      chat.on({
        type: BusEventType.HISTORY_PANEL_NEW_CHAT,
        handler: () => void startNewChat(),
      });
      chat.on({
        type: BusEventType.PRE_SEND,
        handler: (event) => {
          const { data } = event as BusEventPreSend;
          const starter = agent.starters?.find(
            (candidate) => candidate.title === data.input.text
          );
          if (starter) data.input.text = starter.prompt;
        },
      });
      onInstanceReady?.(chat);
    },
    [agent.starters, onInstanceReady, startNewChat]
  );

  if (!open) return null;

  return (
    <AgentDock width={width} onResize={setWidth}>
      <div className={styles.surface}>
        {children}
        <ChatCustomElement
          className={styles.chat}
          messaging={messaging}
          header={header}
          homescreen={homescreen}
          history={history}
          launcher={launcher}
          renderWriteableElements={renderWriteableElements}
          assistantName={assistantName}
          assistantAvatarUrl={InsightsAgentSvg}
          input={inputConfig}
          strings={strings}
          openChatByDefault
          onBeforeRender={handleBeforeRender}
          onAfterRender={(chat) => {
            // Carbon persists view state per session, so a previously-closed
            // chat stays closed on the next mount despite openChatByDefault.
            // This has to wait for onAfterRender: calling it during
            // onBeforeRender lands inside Carbon's own initial view change and
            // throws "view may not be changed while a view change is running".
            if (!chat.getState().viewState.mainWindow) {
              void chat.changeView(ViewType.MAIN_WINDOW);
            }
          }}
          onViewChange={(event: BusEventViewChange) => {
            // Carbon's own close control drives the dock's open state — but
            // only on a real open→closed transition. The chat starts closed and
            // emits a view change while `openChatByDefault` takes effect, so
            // reacting to any non-open state closes the dock on mount.
            if (
              event.oldViewState.mainWindow &&
              !event.newViewState.mainWindow
            ) {
              setOpen(false);
            }
          }}
        />
      </div>
    </AgentDock>
  );
};
